import { formatDecimal, tt } from '@agos/shared'

/**
 * `/mobile-app` — the driver APK, and the build button behind it.
 *
 * **§9.6 supersedes this page**, and the removal is the owner's to sign off (`FRONTEND-STATUS.md`), so it is
 * ported rather than dropped — and it says what it is.
 *
 * **FB-164.** The page cannot build anything on this deployment and does not say so.
 * `GET /apk/build/mode` answers
 *
 * ```json
 * { "mode": "local-eas", "hasExpoToken": false,
 *   "driverAppDir": "/workspace/artifacts/driver-app", "driverAppPresent": false }
 * ```
 *
 * `driverAppPresent: false` — the source is not mounted, so `eas build` has nothing to build. Legacy's
 * `BuildMode` interface declares `{ mode, hasExpoToken, note }`: it **ignores `driverAppPresent`**, the one
 * field that answers *can this button work*, and **reads `note`**, which the backend does not send. Two
 * mismatches in a four-field object, and the button looks ready in both.
 *
 * **FB-165.** `POST /apk/upload-url` answers `uploadUrl: "/api/apk/upload-direct"` and the endpoint is mounted
 * at **`/api/v1/apk/upload-direct`**. A client that follows the answer posts to a path that does not exist —
 * the same missing `/v1` as FB-156's webhook URL, this time in the backend. The page posts to the path it
 * knows and the mismatch is filed.
 *
 * **The build history is not a record.** `ApkBuilds`' own docblock: *"History is in memory and shallow: twenty
 * builds, then the oldest is dropped. It is a progress display, not a record."* Legacy renders it as a build
 * list with expandable logs; a restart empties it, and nothing on screen says so.
 */

export interface ApkInfo {
  filename: string
  sizeBytes: number
  uploadedAt: string
}

export interface BuildMode {
  mode: 'remote-eas' | 'local-eas'
  hasExpoToken: boolean
  driverAppDir: string
  /** The field legacy's interface leaves out. */
  driverAppPresent: boolean
}

export type BuildStatus = 'queued' | 'running' | 'finished' | 'errored'

export interface BuildRecord {
  id: string
  mode: string
  status: BuildStatus
  startedAt: string
  finishedAt: string | null
  log: string
  apkUrl: string | null
  error: string | null
}

/** `ApkBuilds` keeps twenty and drops the oldest. */
export const HISTORY_LIMIT = 20

/** Legacy's interval, kept — and bounded, which legacy is not. */
export const BUILD_POLL_MS = 3_000

/** Twenty minutes at three seconds: longer than a remote queue, short enough to stop. */
export const BUILD_POLL_LIMIT = 400

function text(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null
}

/** `GET /apk/info` answers an error object when nothing has been uploaded, not a 404. */
export function apkInfoOf(payload: unknown): ApkInfo | null {
  if (payload === null || payload === undefined || typeof payload !== 'object') return null
  const row = payload as Record<string, unknown>
  const filename = text(row.filename)
  if (filename === null) return null
  return {
    filename,
    sizeBytes: typeof row.size === 'number' ? row.size : typeof row.sizeBytes === 'number' ? row.sizeBytes : 0,
    uploadedAt: text(row.uploadedAt) ?? '',
  }
}

export function modeOf(payload: unknown): BuildMode | null {
  if (payload === null || payload === undefined || typeof payload !== 'object') return null
  const row = payload as Record<string, unknown>
  return {
    mode: row.mode === 'remote-eas' ? 'remote-eas' : 'local-eas',
    hasExpoToken: row.hasExpoToken === true,
    driverAppDir: text(row.driverAppDir) ?? '',
    driverAppPresent: row.driverAppPresent === true,
  }
}

export function buildsOf(payload: unknown): BuildRecord[] {
  if (!Array.isArray(payload)) return []
  return payload.flatMap((entry) => {
    const row = entry as Record<string, unknown>
    const id = text(row.id)
    if (id === null) return []
    const status = row.status
    return [
      {
        id,
        mode: text(row.mode) ?? '',
        status:
          status === 'queued' || status === 'running' || status === 'finished' || status === 'errored'
            ? status
            : 'queued',
        startedAt: text(row.startedAt) ?? '',
        finishedAt: text(row.finishedAt),
        log: typeof row.log === 'string' ? row.log : '',
        apkUrl: text(row.apkUrl),
        error: text(row.error),
      },
    ]
  })
}

// ─── FB-164: whether the button can work at all ──────────────────────────────

export type BuildReadiness = 'no-source' | 'local' | 'remote' | 'unknown'

/**
 * Whether a build can be started, and how it would run.
 *
 * The source directory decides it: without a checkout there is nothing for `eas build` to build, whatever the
 * mode says. `ApkBuilds` reports each missing piece as a failed build with a message rather than a crash —
 * this decides whether to press the button at all.
 */
export function readinessOf(mode: BuildMode | null): BuildReadiness {
  if (mode === null) return 'unknown'
  if (!mode.driverAppPresent) return 'no-source'
  return mode.hasExpoToken ? 'remote' : 'local'
}

export function readinessMessage(readiness: BuildReadiness, mode: BuildMode | null): string {
  if (readiness === 'unknown') return tt('Stav zostavovania sa nedá zistiť.')
  if (readiness === 'no-source') {
    const where = mode?.driverAppDir ?? ''
    return `${tt('Zdroj šoférskej aplikácie nie je pripojený')} (${where}) - ${tt('zostaviť sa nedá nič.')}`
  }
  if (readiness === 'remote') {
    return tt('Zostavuje sa na serveroch Expo. Tlačidlo sa vráti hneď, ako sa build zaradí do frontu.')
  }
  return tt(
    'Zostavuje sa lokálne, bez tokenu Expo - trvá to dlho a APK sa zapíše priamo na server.',
  )
}

export function mayBuild(readiness: BuildReadiness): boolean {
  return readiness === 'local' || readiness === 'remote'
}

// ─── The history ─────────────────────────────────────────────────────────────

export function historyNote(builds: readonly BuildRecord[]): string {
  const base = tt(
    'História je v pamäti servera a drží posledných 20 behov - po restarte je prázdna. Je to zobrazenie priebehu, nie záznam.',
  )
  if (builds.length >= HISTORY_LIMIT) {
    return `${base} ${tt('Je plná, takže starší beh už tu nenájdete.')}`
  }
  return base
}

export function activeBuild(builds: readonly BuildRecord[]): BuildRecord | null {
  return builds.find((build) => build.status === 'queued' || build.status === 'running') ?? null
}

export function buildStatusLabel(status: BuildStatus): string {
  if (status === 'queued') return tt('Vo fronte')
  if (status === 'running') return tt('Beží')
  if (status === 'finished') return tt('Hotové')
  return tt('Zlyhalo')
}


/** Twenty minutes, as `BUILD_POLL_LIMIT` attempts at `BUILD_POLL_MS` always meant. */
export const BUILD_POLL_GIVE_UP_MS = BUILD_POLL_LIMIT * BUILD_POLL_MS

/**
 * Whether a build has been watched long enough to stop watching — §9.35.
 *
 * The page polled for as long as any build was `queued` or `running`, with no other stop, so a build
 * stuck in a remote queue was asked about every three seconds for as long as the tab stayed open.
 *
 * **Measured from the build's own `startedAt` rather than from a count of attempts**, which is what
 * `pollGaveUp(attempts)` took and why that one is gone. An age needs no counter, so there is no state
 * to update while deciding whether to poll — and a counter in that position is exactly what span the
 * AI-translate poll to its cap. It is also the truer rule: closing the tab and reopening it restarts a
 * count and does not restart the build.
 *
 * Unparseable or missing `startedAt` answers **false** — keep polling. A build nobody can date is not
 * evidence that it is stuck, and refusing to watch it would be a worse failure than watching too long.
 */
export function pollExpired(build: BuildRecord, nowMs: number): boolean {
  const started = Date.parse(build.startedAt)
  if (!Number.isFinite(started)) return false
  return nowMs - started >= BUILD_POLL_GIVE_UP_MS
}

/** Said when the watch stops on a build the server never finished. */
export function pollGaveUpNote(): string {
  return tt('Zostavenie trvá príliš dlho - prestali sme ho sledovať. Obnovte stránku pre nový stav.')
}

// ─── The upload ──────────────────────────────────────────────────────────────

/**
 * Where an APK is posted.
 *
 * `POST /apk/upload-url` used to answer `/api/apk/upload-direct` — a path that does not exist, because the
 * endpoint is mounted with the `/v1` every other route carries, so a client that honoured the answer
 * uploaded into a 404 (FB-165). **The endpoint answers the mounted path now**, and the two agree.
 *
 * Kept as a constant rather than read from the response: the upload is a two-step dance only because
 * legacy signed an object-storage URL, and this deployment has no object storage. Following a path an
 * endpoint hands back is how the mismatch became invisible in the first place.
 */
export const UPLOAD_PATH = '/api/v1/apk/upload-direct'

/** What it used to answer, so the test shows the difference rather than describing it. */
export const PREVIOUSLY_ADVERTISED_UPLOAD_PATH = '/api/apk/upload-direct'

export const APK_MEDIA_TYPE = 'application/vnd.android.package-archive'

export type UploadProblem = 'not-apk' | 'empty' | 'too-large' | null

/** The server's own cap, so a file it will refuse is refused before the upload starts. */
export const MAX_APK_BYTES = 300 * 1024 * 1024

export function uploadProblem(file: { name: string; size: number }): UploadProblem {
  if (!file.name.toLowerCase().endsWith('.apk')) return 'not-apk'
  if (file.size <= 0) return 'empty'
  return file.size > MAX_APK_BYTES ? 'too-large' : null
}

export function uploadProblemMessage(problem: UploadProblem): string {
  if (problem === 'not-apk') return tt('Súbor musí mať príponu .apk.')
  if (problem === 'empty') return tt('Súbor je prázdny.')
  if (problem === 'too-large') return `${tt('Súbor je väčší ako')} ${formatSize(MAX_APK_BYTES)}.`
  return ''
}

/** Binary units with the labels that mean them, and a Slovak decimal — as `/settings-backups` does. */
export function formatSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '-'
  if (bytes < 1024) return `${formatDecimal(bytes, 0)} B`
  if (bytes < 1024 * 1024) return `${formatDecimal(bytes / 1024, 0)} KiB`
  return `${formatDecimal(bytes / 1024 / 1024, 1)} MiB`
}

// ─── §9.6 ────────────────────────────────────────────────────────────────────

/**
 * What this screen is for, now that the driving surface lives in the manager.
 *
 * FRONTEND.md §9.6 moves the driver app into the website; `FRONTEND-STATUS.md` records that removing this page
 * needs the owner's explicit sign-off. So it stays, and says both.
 */
export function retirementNote(): string {
  return tt(
    'Šoférska appka sa podľa plánu presúva do manažéra - táto obrazovka je tu dovtedy, kým to majiteľ nepotvrdí. Nová appka sa medzitým nemusí zostavovať.',
  )
}
